import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {
  IFilterCriteria,
  IProductFeedLuceneSnapshotDocument,
  IProductFeedOptions,
  IProductFeedRouteRule,
  ITransformPattern,
  Transform
} from '../../../../models/product-feed-options.model';
import {MaterialModule} from '../../../../material.module';
import {DatePipe} from '@angular/common';
import {ToastService} from '../../../../services/layout/toast.service';
import {ProductFeedService} from '../../../../services/objects/product-feed.service';
import {MatDialog} from '@angular/material/dialog';
import {RouterLink} from '@angular/router';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {
  ProductFeedGeneralSettingsComponent
} from '../product-feed-general-settings/product-feed-general-settings.component';
import {FilterCriteriaComponent} from '../filter-criteria/filter-criteria.component';
import {
  TransformPatternComponent,
  validateTransformConfiguration
} from '../field-mapping/transform-pattern.component';
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {ScheduleSettingsComponent} from "../../../objects/scheduling/schedule-settings.component";
import {Subject, takeUntil, takeWhile, timer} from "rxjs";
import {switchMap} from "rxjs/operators";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";
import {NotificationSettingsComponent} from "../../../objects/notifications/notification-settings.component";
import {ProductQueryTestComponent} from "../../../product-query-test/product-query-test.component";
import {ProductFeedTask} from '../../../../models/scheduled-task.model';
import {
  createFieldMappingEntryGroup,
  maximumArrayLengthValidator,
  maximumFieldMappingFields,
  ProductFeedFieldMapping,
  StandardFieldMappingComponent,
  uniqueChildKeyValidator
} from '../standard-field-mapping/standard-field-mapping.component';
import {
  ProductLookupDiagnosticComponent
} from '../product-lookup-diagnostic/product-lookup-diagnostic.component';
import {ScheduleSettings} from '../../../../models/schedulable-task-options.model';
import {EnvironmentService} from '../../../../services/environment.service';

@Component({
  selector: 'app-product-feed-settings',
  templateUrl: './product-feed-settings.component.html',
  styleUrls: ['./product-feed-settings.component.scss'],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    ProductFeedGeneralSettingsComponent,
    FilterCriteriaComponent,
    DatePipe,
    TransformPatternComponent,
    ScheduleSettingsComponent,
    ClientObjectSettings,
    NotificationSettingsComponent,
    ProductQueryTestComponent,
    StandardFieldMappingComponent,
    ProductLookupDiagnosticComponent,
    RouterLink
  ],
  standalone: true
})
export class ProductFeedSettingsComponent extends TabbedPageComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private productFeedService = inject(ProductFeedService);
    private snackBar = inject(ToastService);
    private dialog = inject(MatDialog);
    private environmentService = inject(EnvironmentService);


  isSaving: boolean = false;
  isDeleting: boolean = false;

  productFeed: IProductFeedOptions | null = null;
  id: string = '';

  // Reactive Forms
  settingsForm: FormGroup;
  scheduleSettingsForm: FormGroup;
  notificationSettingsForm: FormGroup;
  transformPatternForm: FormGroup;
  filtersForm: FormGroup;
  fieldMappingForm: FormGroup;
  fieldMappingDefaults: ProductFeedFieldMapping = {};
  runningTests = false;
  runningTask: ProductFeedTask | null = null;
  allTasks: ProductFeedTask[] = [];
  availableSourceFeeds: IProductFeedOptions[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly stopRunPolling$ = new Subject<void>();
  private readonly safeRunReasons = new Set([
    'cleanup_deferred',
    'crawl_failed',
    'execution_failed',
    'immutable_name_collision',
    'invalid_feed_association',
    'local_activation_failed',
    'local_hydration_timeout',
    'local_immutable_name_collision',
    'no_pages_crawled',
    'no_products_indexed',
    'no_products_transformed',
    'operation_canceled',
    'partial_ingestion_requires_scope_writer',
    'product_document_rejected',
    'publication_failed',
    'publication_lease_timeout',
    'publication_visible_cleanup_deferred',
    'remote_snapshot_invalid',
    'transform_failed',
    'unknown_product_feed'
  ]);

  ngOnInit(): void {
    super.OnInit()

    this.scheduleSettingsForm = this.fb.group({});
    this.notificationSettingsForm = this.fb.group({});
    this.settingsForm = this.fb.group({
      type: ['Url', Validators.required],
      skipForPageCollection: [false],
      sourceFeedId: [''],
      detailHostAllowlist: this.fb.array([]),
      url: ['', [Validators.required, Validators.pattern('https?://.+')]],
      listingsDirectory: [''],
      stripNamespace: [''],
      urlProperty: [''],
      crawlRegexPattern: [''],
      urlRegexPattern: [''],
      urlRegexReplacePattern: [''],
        customCategorySyntax: [''],
        defaultDomain: [''],
        brandDomain: [''],
        market: [''],
        productSearchProgressText: ['', this.validateProductSearchProgressText()],
        isDefaultFeed: [false],
        routeRules: this.fb.array([]),
      }, {validators: this.validateFeedGraph()});

    this.filtersForm = this.fb.group({
      filters: this.fb.array([])
    })

    this.fieldMappingForm = this.fb.group({
      fields: this.fb.array([], [
        uniqueChildKeyValidator('outputName'),
        maximumArrayLengthValidator(maximumFieldMappingFields)
      ])
    });

    this.transformPatternForm = this.fb.group({
      patterns: this.fb.array([]),
    });

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.stopRunPolling$.next();
    this.stopRunPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private validateProductSearchProgressText(): (control: AbstractControl) => ValidationErrors | null {
    return control => {
      const raw = String(control.value ?? '');
      const normalized = raw.trim();

      if (normalized.length === 0) return null;
      if (normalized.length > 160) {
        return {productSearchProgressTextTooLong: true};
      }

      if (this.hasControlCharacters(raw)) {
        return {productSearchProgressTextContainsControlCharacters: true};
      }

      if (/<[^>]*>/i.test(raw) || /\[[^\]\n]+]\([^)\n]+\)/.test(raw) ||
          /^\s{0,3}#{1,6}\s+\S/m.test(raw) ||
          /^\s{0,3}[-+*]\s+\S/m.test(raw) ||
          /^\s{0,3}\d+\.\s+\S/m.test(raw) ||
          /^\s{0,3}>\s+\S/m.test(raw) ||
          /\bhttps?:\/\/\S+/i.test(raw) ||
          /\bwww\.[\w.-]+\.[a-z]{2,}(?:\/\S*)?/i.test(raw) ||
          raw.includes('**') || raw.includes('__') || raw.includes('`')) {
        return {productSearchProgressTextContainsMarkup: true};
      }

      return null;
    };
  }

  private validateFeedGraph(): (control: AbstractControl) => ValidationErrors | null {
    return control => {
      const sourceFeedId = String(control.get('sourceFeedId')?.value ?? '').trim();
      if (!sourceFeedId) {
        return String(control.get('defaultDomain')?.value ?? '').trim()
          ? null
          : {rootDefaultDomainRequired: true};
      }

      const type = String(control.get('type')?.value ?? '');
      const skipForPageCollection = control.get('skipForPageCollection')?.value === true;
      const detailHosts = control.get('detailHostAllowlist') as FormArray | null;
      if (type !== 'Json' || !skipForPageCollection || !detailHosts || detailHosts.length === 0) {
        return {invalidDetailFeedGraph: true};
      }

      return null;
    };
  }

  private hasControlCharacters(value: string): boolean {
    for (let i = 0; i < value.length; i += 1) {
      const code = value.charCodeAt(i);
      if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) {
        return true;
      }
    }

    return false;
  }

  get filters(): FormArray {
    return this.filtersForm.get('filters') as FormArray;
  }

  get detailHostAllowlist(): FormArray {
    return this.settingsForm.get('detailHostAllowlist') as FormArray;
  }

  private createDetailHost(host = '') {
    return this.fb.control(host, [
      control => {
        const value = String(control.value ?? '').trim();
        if (!value) return {required: true};
        if (!this.isValidDetailHost(value)) {
          return {invalidHost: true};
        }
        return null;
      }
    ]);
  }

  private isValidDetailHost(value: string): boolean {
    if (/[*/?#@]/.test(value)) return false;

    try {
      const url = new URL(`https://${value}`);
      const host = url.hostname.replace(/^\[|\]$/g, '');
      return !!host && !this.isIpAddress(host);
    } catch {
      return false;
    }
  }

  private isIpAddress(host: string): boolean {
    return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) || /^[0-9a-f:]+$/i.test(host);
  }

  createTransformPattern(pattern: ITransformPattern | undefined): FormGroup {
    const group = this.fb.group({
      urlPattern: [pattern?.urlPattern ?? '', Validators.required],
      transforms: this.fb.array([])
    });

    const transforms = group.get('transforms') as FormArray;
    transforms.clear();
    pattern?.transforms?.forEach(x => {
      transforms.push(this.createTransform(x));
    })
    return group;
  }

  private createTransform(transforms: Transform): FormGroup {
    const group = this.fb.group({
      transformer: [transforms?.transformer ?? '', Validators.required],
      name: [transforms?.name ?? '', Validators.required],
      primaryNode: [transforms?.primaryNode ?? ''],
      // Initialize properties as a FormGroup
      properties: this.fb.group({})
    }, {validators: validateTransformConfiguration});

    const properties = group.get('properties') as FormGroup;

    // Populate properties with key-value pairs
    if (transforms?.properties) {
      Object.entries(transforms.properties).forEach(([key, value]) => {
        properties.addControl(key, this.fb.control(value, Validators.required));
      });
    }

    return group;
  }


  createFilter(criteria?: IFilterCriteria | undefined): FormGroup {
    return this.fb.group({
      propertyName: [criteria?.propertyName ?? '', Validators.required],
      operation: [criteria?.operation ?? '', Validators.required],
      value: [criteria?.value ?? '', Validators.required]
    });
  }

  createRouteRule(rule?: IProductFeedRouteRule): FormGroup {
    return this.fb.group({
      domainPattern: [rule?.domainPattern ?? ''],
      pathPattern: [rule?.pathPattern ?? '*'],
      priority: [rule?.priority ?? 0]
    });
  }

  /**
   * Loads the product feed data and populates the form.
   */
  async loadData() {
    try {
      // Fetch Product Feed Data
      const response = await this.productFeedService.getByIdAsync(this.id).toPromise();
      this.productFeed = response as IProductFeedOptions;
      this.populateForms(this.productFeed);
      if (this.isDetailFeed) {
        this.allTasks = [];
        await Promise.all([this.loadAvailableSourceFeeds(), this.loadFieldMappingDefaults()]);
      } else {
        await Promise.all([this.loadTasks(), this.loadAvailableSourceFeeds(), this.loadFieldMappingDefaults()]);
      }

    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }

  private populateForms(productFeed: IProductFeedOptions): void {
    this.settingsForm.patchValue({
      type: productFeed.type,
      skipForPageCollection: productFeed.skipForPageCollection,
      sourceFeedId: productFeed.sourceFeedId ?? '',
      url: productFeed.url,
      listingsDirectory: productFeed.listingsDirectory,
      stripNamespace: productFeed.stripNamespace,
      urlProperty: productFeed.urlProperty,
      crawlRegexPattern: productFeed.crawlRegexPattern,
      urlRegexPattern: productFeed.urlRegexPattern,
      urlRegexReplacePattern: productFeed.urlRegexReplacePattern,
      customCategorySyntax: productFeed.customCategorySyntax,
      defaultDomain: productFeed.defaultDomain,
      brandDomain: productFeed.brandDomain ?? '',
      market: productFeed.market ?? '',
      productSearchProgressText: productFeed.productSearchProgressText ?? '',
      isDefaultFeed: productFeed.isDefaultFeed ?? false,
    });

    this.detailHostAllowlist.clear();
    (productFeed.detailHostAllowlist ?? []).forEach(host => {
      this.detailHostAllowlist.push(this.createDetailHost(host));
    });

    const routeRulesArray = this.settingsForm.get('routeRules') as FormArray;
    routeRulesArray.clear();
    (productFeed.routeRules ?? []).forEach(rule => {
      routeRulesArray.push(this.createRouteRule(rule));
    });

    const filtersArray = this.filtersForm.get('filters') as FormArray;
    filtersArray.clear();
    (productFeed.filters ?? []).forEach(filter => {
      filtersArray.push(this.createFilter(filter));
    });

    const patternsArray = this.transformPatternForm.get('patterns') as FormArray;
    patternsArray.clear();
    (productFeed.transformPatterns ?? []).forEach(pattern => {
      patternsArray.push(this.createTransformPattern(pattern));
    });

    const fieldMappingArray = this.fieldMappingForm.get('fields') as FormArray;
    fieldMappingArray.clear();
    Object.entries(productFeed.fieldMapping ?? {}).forEach(([outputName, properties]) => {
      fieldMappingArray.push(createFieldMappingEntryGroup(this.fb, outputName, properties));
    });
  }

  private async loadFieldMappingDefaults(): Promise<void> {
    try {
      this.fieldMappingDefaults = await this.productFeedService.getFieldMappingDefaultsAsync().toPromise() ?? {};
    } catch {
      this.fieldMappingDefaults = {};
      this.snackBar.showError('Default field mapping is unavailable', 4000);
    }
  }

  private async loadTasks(): Promise<void> {
    try {
      const allTasks = await this.productFeedService.getAllTasksAsync(this.id).toPromise();
      this.allTasks = ((allTasks as ProductFeedTask[]) ?? [])
        .sort((left, right) => new Date(right.created).getTime() - new Date(left.created).getTime());
      this.resumeOutstandingRun();
    } catch (error) {
      console.error('Error loading product feed tasks', error);
      this.allTasks = [];
      this.snackBar.showError('Error loading product feed task history', 3000);
    }
  }

  private async loadAvailableSourceFeeds(): Promise<void> {
    try {
      const feeds = await this.productFeedService.getAllAsync().toPromise();
      const currentRootId = String(this.productFeed?.sourceFeedId ?? '').trim();
      const occupiedRootIds = new Set((feeds ?? [])
        .filter(feed => feed.id !== this.id)
        .map(feed => String(feed.sourceFeedId ?? '').trim())
        .filter(Boolean));
      this.availableSourceFeeds = (feeds ?? [])
        .filter(feed => feed.id !== this.id)
        .filter(feed => feed.enabled)
        .filter(feed => feed.type?.toLowerCase() === 'xml' && !feed.skipForPageCollection && !feed.sourceFeedId)
        .filter(feed => feed.id === currentRootId || !occupiedRootIds.has(feed.id))
        .sort((left, right) => (left.name || left.id).localeCompare(right.name || right.id));
    } catch (error) {
      console.error('Error loading source feed candidates', error);
      this.availableSourceFeeds = [];
      this.snackBar.showError('Error loading feed graph options', 3000);
    }
  }

  isFormTouched(): boolean {
    if (this.scheduleSettingsForm.touched) return true;
    if (this.notificationSettingsForm.touched) return true;
    if (this.settingsForm.touched) return true;
    if (this.transformPatternForm.touched) return true;
    if (this.filtersForm.touched) return true;
    if (this.fieldMappingForm.touched) return true;
    return false;
  }

  onFilterChange(updatedFilter: IFilterCriteria[]) {
    this.settingsForm.patchValue({
      filters: updatedFilter
    });
  }

  /**
   * Saves the product feed settings.
   */
  save() {
    if (!this.formsAreValid()) {
      this.settingsForm.markAllAsTouched();
      this.scheduleSettingsForm.markAllAsTouched();
      this.notificationSettingsForm.markAllAsTouched();
      this.transformPatternForm.markAllAsTouched();
      this.filtersForm.markAllAsTouched();
      this.fieldMappingForm.markAllAsTouched();
      this.snackBar.showError('Please fix the errors in the form before saving.', 3000);
      return;
    }

    if (!this.productFeed) return;
    if (!this.productFeed._etag) {
      this.snackBar.showError('This feed cannot be saved safely without a current version. Reload and try again.', 5000);
      return;
    }
    this.isSaving = true;

    const updatedProductFeed: IProductFeedOptions = {
      ...this.productFeed,
      ...this.settingsForm.getRawValue()
    };

    if (Object.keys(this.scheduleSettingsForm.controls).length > 0) {
      updatedProductFeed.scheduleSettings = {
        ...updatedProductFeed.scheduleSettings,
        ...this.scheduleSettingsForm.getRawValue()
      };
    }

    if (Object.keys(this.notificationSettingsForm.controls).length > 0) {
      updatedProductFeed.notificationSettings = {
        ...updatedProductFeed.notificationSettings,
        ...this.notificationSettingsForm.getRawValue()
      };
    }

    if (this.transformPatternForm.dirty || this.transformPatternForm.touched) {
      const patterns = this.transformPatternForm.getRawValue().patterns ?? [];
      updatedProductFeed.transformPatterns = patterns;
    }

    if (this.filtersForm.dirty || this.filtersForm.touched) {
      updatedProductFeed.filters = this.filtersForm.getRawValue().filters ?? [];
    }

    if (this.fieldMappingForm.dirty) {
      updatedProductFeed.fieldMapping = this.serializeFieldMapping();
    }

    this.productFeed = updatedProductFeed;
    const savePayload = this.createSavePayload(updatedProductFeed);

    this.productFeedService.replaceAsync(savePayload).subscribe({
      next: response => {
        this.productFeed = {...updatedProductFeed, _etag: response.etag};
        this.markFormsPristine();
        this.snackBar.showSuccess(
          response.cacheRefreshSucceeded
            ? 'Settings saved successfully'
            : 'Settings saved; runtime cache refresh is pending',
          4000);
        this.isSaving = false;
      },
      error: error => this.handleSaveError(error)
    });
  }

  private handleSaveError(error: any): void {
    console.error('Error saving settings', error);
    const errorCode = String(error?.error?.error ?? '').trim();
    this.snackBar.showError(
      error?.status === 412
        ? 'This feed changed in another session. Reload before saving again.'
        : errorCode === 'invalid_product_feed_field_mapping'
          ? 'Field mapping is invalid or exceeds the safe editing limits.'
          : 'Error saving settings',
      5000);
    this.isSaving = false;
  }

  /**
   * Deletes the product feed.
   */
  delete() {
    if (!this.productFeed) return;
    if (!this.productFeed._etag) {
      this.snackBar.showError('This feed cannot be deleted safely without a current version. Reload and try again.', 5000);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this product feed?',
        message: 'This will delete the product feed record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting product feed...', 3000);
        this.productFeedService.deleteIfMatchAsync(this.productFeed!.id, this.productFeed!._etag!).subscribe(() => {
          this.snackBar.showSuccess('Product feed deleted successfully.', 3000);
          this.router.navigate(['/ai/product-feeds']);
        }, error => {
          console.error('Error deleting product feed', error);
          this.snackBar.showError(
            error?.status === 412
              ? 'This feed changed in another session. Reload before deleting.'
              : 'Error deleting product feed.',
            5000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }



  runFullFeed() {
    if (!this.canRunFeed) {
      this.snackBar.showError(
        this.isDetailFeed
          ? 'Run the feed from its root feed.'
          : 'Enable and save the root feed before running it.',
        4000);
      return;
    }

    if (this.isFormTouched()) {
      this.snackBar.showError('Save or discard your changes before running the feed.', 4000);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Run the complete feed now?',
        message: 'This fetches the complete configured feed graph and publishes a new index generation. If the run produces no safe products, the current index is preserved.',
        confirmText: 'Run full feed',
        confirmButtonText: 'Run feed',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      this.snackBar.showInfo('Full feed run queued', 3000);
      this.runningTests = true;
      this.stopRunPolling$.next();
      this.productFeedService.runProductFeedAsync(this.id).subscribe({
        next: initialTask => {
          this.startRunPolling(initialTask as ProductFeedTask);
        },
        error: error => this.handleRunError(error)
      });
    });
  }

  isTaskTerminal(task: ProductFeedTask | null | undefined): boolean {
    return !!task?.completed;
  }

  private handleRunError(error: any): void {
    console.error('Error running product feed', error);
    this.runningTests = false;
    this.snackBar.showError('Unable to run the product feed', 5000);
  }

  private resumeOutstandingRun(): void {
    if (this.isDetailFeed || this.runningTests) return;

    const outstandingTask = this.allTasks.find(task =>
      this.shouldPollTask(task) &&
      !this.isLegacyTaskTerminal(task) &&
      !(task.id === this.runningTask?.id && this.isTaskTerminal(this.runningTask)));
    if (outstandingTask) {
      this.startRunPolling(outstandingTask);
    }
  }

  private startRunPolling(task: ProductFeedTask): void {
    this.stopRunPolling$.next();
    this.runningTask = task;
    this.runningTests = true;

    timer(0, 10000).pipe(
      switchMap(() => this.productFeedService.getTaskAsync(this.id, task.id)),
      takeWhile(updatedTask => this.shouldPollTask(updatedTask as ProductFeedTask), true),
      takeUntil(this.stopRunPolling$),
      takeUntil(this.destroy$)
    ).subscribe({
      next: updatedTask => {
        this.runningTask = updatedTask as ProductFeedTask;
        if (this.shouldPollTask(this.runningTask)) return;

        this.runningTests = false;
        if (this.isTaskTerminal(this.runningTask)) {
          void this.refreshAfterRun();
        }
        if (this.runningTask.completedSuccessfully && this.isTaskTerminal(this.runningTask)) {
          this.snackBar.showSuccess('Full feed published successfully', 4000);
        } else if (this.runningTask.executionState === 'retry_pending') {
          this.snackBar.showInfo('Feed run is awaiting a retry by the feed runner.', 6000);
        } else {
          this.snackBar.showError(`Feed run failed: ${this.safeTaskReason(this.runningTask)}`, 6000);
        }
      },
      error: error => this.handleRunError(error)
    });
  }

  get feedGraphRole(): string {
    return this.productFeed?.sourceFeedId ? 'Detail feed' : 'Root / standalone feed';
  }

  get isDetailFeed(): boolean {
    return !!this.detailSourceFeedId;
  }

  get detailSourceFeedId(): string {
    return String(this.settingsForm?.get('sourceFeedId')?.value ?? this.productFeed?.sourceFeedId ?? '').trim();
  }

  get rootFeed(): IProductFeedOptions | null {
    if (!this.isDetailFeed) return this.productFeed;
    return this.availableSourceFeeds.find(feed => feed.id === this.detailSourceFeedId) ?? null;
  }

  get rootFeedName(): string {
    return this.rootFeed?.name || this.detailSourceFeedId || 'configured root feed';
  }

  get canRunFeed(): boolean {
    return !!this.productFeed?.enabled && !this.isDetailFeed;
  }

  get hasAutomaticSchedule(): boolean {
    const mode = String(this.productFeed?.scheduleSettings?.schedulingMode ?? '').trim();
    return !!this.productFeed?.enabled &&
      !!this.productFeed?.scheduleSettings?.scheduleEnabled &&
      mode.length > 0 &&
      mode.toLowerCase() !== 'manual' &&
      !this.isDetailFeed;
  }

  get scheduleStatus(): string {
    if (this.isDetailFeed) return 'Root-owned';
    if (!this.productFeed?.enabled) return 'Feed disabled';
    if (!this.hasAutomaticSchedule) return 'Manual only';
    return this.environmentService.production ? 'Enabled' : 'Configured (production only)';
  }

  get showNextScheduledExecution(): boolean {
    return this.hasAutomaticSchedule && this.environmentService.production;
  }

  get showNonProductionScheduleNotice(): boolean {
    return this.hasAutomaticSchedule && !this.environmentService.production;
  }

  get sourceFeedName(): string {
    const sourceFeedId = this.productFeed?.sourceFeedId;
    if (!sourceFeedId) return '';
    const source = this.availableSourceFeeds.find(feed => feed.id === sourceFeedId);
    return source?.name || sourceFeedId;
  }

  get hasResolvedSourceFeed(): boolean {
    const sourceFeedId = this.productFeed?.sourceFeedId;
    return !sourceFeedId || this.availableSourceFeeds.some(feed => feed.id === sourceFeedId);
  }

  get indexStatus(): string {
    if (!this.productFeed?.indexGenerationId) return 'Not published';
    if (!this.productFeed.luceneSnapshot) return 'Published; count unavailable';
    return this.productFeed.luceneSnapshot.totalDocumentCount > 0 ? 'Ready' : 'Published without products';
  }

  get confirmedFetchDate(): string | null {
    const value = String(this.productFeed?.lastFetchDateTime ?? '').trim();
    return value && Number.isFinite(Date.parse(value)) ? value : null;
  }

  get indexedDocumentCount(): number | null {
    return this.productFeed?.luceneSnapshot?.totalDocumentCount ?? null;
  }

  get latestTask(): ProductFeedTask | null {
    return this.allTasks[0] ?? null;
  }

  get readinessIssues(): string[] {
    if (!this.productFeed) return ['Feed configuration is unavailable.'];

    const issues: string[] = [];
    if (!this.productFeed.enabled) issues.push('The feed is disabled.');
    if (!this.hasResolvedSourceFeed) issues.push('The configured XML source feed cannot be resolved.');
    if (!this.productFeed.indexGenerationId) issues.push('No published index generation is recorded.');
    if (this.productFeed.luceneSnapshot?.totalDocumentCount === 0) {
      issues.push('No indexed products are visible in the latest snapshot.');
    }
    if (this.latestTask && this.isTaskTerminal(this.latestTask) && !this.latestTask.completedSuccessfully) {
      issues.push(`The latest run failed (${this.safeTaskReason(this.latestTask)}).`);
    }
    return issues;
  }

  get showSaveActions(): boolean {
    return (this.isDetailFeed ? [2, 3, 4] : [2, 3, 4, 5]).includes(this.selectedIndex);
  }

  taskState(task: ProductFeedTask): string {
    if (task.completed) return task.completedSuccessfully ? 'Succeeded' : 'Failed';
    if (this.isLegacyTaskTerminal(task)) {
      return task.completedSuccessfully ? 'Succeeded (legacy)' : 'Failed (legacy)';
    }
    if (task.executionState === 'running') return 'Running';
    if (task.executionState === 'retry_pending') return 'Awaiting retry';
    if (task.executionState === 'queued') return 'Queued';
    if (task.lastAttempt) return 'Running';
    return 'Queued';
  }

  private shouldPollTask(task: ProductFeedTask | null | undefined): boolean {
    return !!task &&
      !this.isTaskTerminal(task) &&
      !this.isLegacyTaskTerminal(task) &&
      task.executionState !== 'retry_pending';
  }

  isLegacyTaskTerminal(task: ProductFeedTask): boolean {
    return !task.completed && !!task.lastAttempt && task.completedSuccessfully === true;
  }

  taskOrigin(task: ProductFeedTask): string {
    return (task as ProductFeedTask & {isScheduled?: boolean}).isScheduled ? 'Scheduled' : 'Manual';
  }

  safeTaskReason(task: ProductFeedTask): string {
    const reason = String(task.zeroResultReason ?? '').trim().toLowerCase();
    return this.safeRunReasons.has(reason) ? reason : (reason ? 'execution_failed' : '—');
  }

  safeTaskAction(task: ProductFeedTask): string {
    const action = String(task.indexAction ?? '').trim().toLowerCase();
    return ['replaced_existing', 'preserved_existing', 'forced_empty'].includes(action) ? action : '—';
  }

  boundedCount(value: number | undefined): number {
    return Math.min(1_000_000_000, Math.max(0, Number.isFinite(value) ? Number(value) : 0));
  }

  taskDuration(task: ProductFeedTask): string {
    if (!task.lastAttempt || !task.completed) return '—';
    const milliseconds = Math.max(0, new Date(task.completed).getTime() - new Date(task.lastAttempt).getTime());
    if (milliseconds < 1000) return '<1s';
    const seconds = Math.round(milliseconds / 1000);
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }

  private formsAreValid(): boolean {
    const fieldMappingValid = !this.fieldMappingForm.dirty || this.fieldMappingForm.valid;
    return this.settingsForm.valid &&
      this.scheduleSettingsForm.valid &&
      this.notificationSettingsForm.valid &&
      this.transformPatternForm.valid &&
      this.filtersForm.valid &&
      fieldMappingValid &&
      this.hasValidDetailTransformation();
  }

  private hasValidDetailTransformation(): boolean {
    const sourceFeedId = String(this.settingsForm.get('sourceFeedId')?.value ?? '').trim();
    if (!sourceFeedId) return true;

    const patterns = this.transformPatternForm.getRawValue().patterns ?? [];
    return patterns.length > 0 &&
      patterns.every((pattern: ITransformPattern) =>
        !!String(pattern.urlPattern ?? '').trim() &&
        (pattern.transforms ?? []).length > 0 &&
        pattern.transforms.every(transform => !!String(transform.transformer ?? '').trim())) &&
      patterns.some((pattern: ITransformPattern) => pattern.urlPattern === '*');
  }

  private async refreshAfterRun(): Promise<void> {
    try {
      const response = await this.productFeedService.getByIdAsync(this.id).toPromise();
      if (response) {
        this.productFeed = response;
        this.populateForms(response);
        this.markFormsPristine();
      }
      await this.loadTasks();
    } catch (error) {
      console.error('Error refreshing product feed after run', error);
      this.snackBar.showError('The run finished, but refreshed readiness data is unavailable.', 5000);
    }
  }

  private createSavePayload(productFeed: IProductFeedOptions): IProductFeedOptions {
    const {luceneSnapshot: _luceneSnapshot, ...payload} = productFeed;
    const normalized = {
      ...payload,
      sourceFeedId: String(payload.sourceFeedId ?? '').trim(),
      detailHostAllowlist: (payload.detailHostAllowlist ?? [])
        .map(host => String(host ?? '').trim().toLowerCase())
        .filter(Boolean)
    } as IProductFeedOptions;
    normalized.scheduleSettings = this.normalizeScheduleSettings(
      normalized.scheduleSettings,
      !!normalized.sourceFeedId);
    return normalized;
  }

  private serializeFieldMapping(): ProductFeedFieldMapping {
    const mapping: ProductFeedFieldMapping = {};
    const fields = this.fieldMappingForm.getRawValue().fields ?? [];
    for (const field of fields) {
      const outputName = String(field.outputName ?? '').trim();
      const properties: Record<string, string> = {};
      for (const property of field.properties ?? []) {
        properties[String(property.key ?? '').trim()] = String(property.value ?? '').trim();
      }
      mapping[outputName] = properties;
    }
    return mapping;
  }

  private normalizeScheduleSettings(
    scheduleSettings: ScheduleSettings,
    isDetailFeed: boolean): ScheduleSettings {
    const mode = String(scheduleSettings?.schedulingMode ?? '').trim();
    if (isDetailFeed || !scheduleSettings?.scheduleEnabled || mode.toLowerCase() === 'manual') {
      return {
        ...scheduleSettings,
        scheduleEnabled: false,
        schedulingMode: '',
        intervalMinutes: 0,
        cronExpression: '',
        startDateUtc: null,
        endDateUtc: null,
        nextExecutionUtc: null
      };
    }

    return {
      ...scheduleSettings,
      schedulingMode: mode,
      intervalMinutes: mode === 'Interval' ? Number(scheduleSettings.intervalMinutes) : 0,
      cronExpression: mode === 'Cron' ? String(scheduleSettings.cronExpression ?? '').trim() : ''
    };
  }

  private markFormsPristine(): void {
    for (const form of [
      this.settingsForm,
      this.scheduleSettingsForm,
      this.notificationSettingsForm,
      this.transformPatternForm,
      this.filtersForm,
      this.fieldMappingForm
    ]) {
      form.markAsPristine();
      form.markAsUntouched();
    }
  }

  get hasLuceneSnapshot(): boolean {
    return !!this.productFeed?.luceneSnapshot;
  }

  get luceneSnapshotSummary(): string[] {
    const snapshot = this.productFeed?.luceneSnapshot;
    if (!snapshot) {
      return [];
    }

    const summary: string[] = [];

    if (snapshot.truncation.hasMoreDocuments) {
      summary.push(`Showing ${snapshot.documents.length} of ${snapshot.totalDocumentCount} indexed documents`);
    }

    if (snapshot.truncation.hasOmittedFields && snapshot.omittedFieldCount > 0) {
      summary.push(`${snapshot.omittedFieldCount} fields omitted`);
    }

    if (snapshot.truncation.hasRedactedValues && snapshot.redactedValueCount > 0) {
      summary.push(`${snapshot.redactedValueCount} values redacted`);
    }

    if (snapshot.truncation.hasTrimmedStrings) {
      summary.push(`String values trimmed to ${snapshot.stringValueLimit} characters`);
    }

    if (snapshot.truncation.hasStructuredValueSummaries) {
      summary.push('Structured values collapsed to summaries');
    }

    if (snapshot.truncation.hitSerializedSizeLimit) {
      summary.push(`Snapshot capped at ${snapshot.serializedJsonLimitBytes / 1024} KiB`);
    }

    return summary;
  }

  luceneDocumentSummary(document: IProductFeedLuceneSnapshotDocument): string[] {
    const summary: string[] = [];

    if (document.omittedFieldCount > 0) {
      summary.push(`${document.omittedFieldCount} fields omitted`);
    }

    if (document.redactedValueCount > 0) {
      summary.push(`${document.redactedValueCount} values redacted`);
    }

    if (document.hasTrimmedStrings) {
      summary.push('Includes trimmed values');
    }

    if (document.hasStructuredValueSummaries) {
      summary.push('Includes summarized structured values');
    }

    return summary;
  }

}
